import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, Select, DatePicker, RNLLoader, Btn, useToast } from '../UI';
import { gridSelectStyle, gridSelectDisabledStyle } from './RequisitionLineItemsTable';
import { errorText } from '../../utils';
import * as api from '../../api';

/**
 * Direct edit of a finished requisition.
 *
 * A page of its own rather than a modal, because what is editable here is the
 * whole requisition — header, every line, and the review figures — and none of
 * that fits in a dialog without becoming a form somebody scrolls blindly.
 *
 * The one thing this screen must never do is move the requisition. It writes
 * data; the approval it already carries stays exactly as it is. That is said
 * out loud at the top of the page rather than left for someone to discover,
 * because "I edited it, did that un-approve it?" is the first question anyone
 * will have.
 */

const money = (v) =>
  v === '' || v == null || isNaN(v)
    ? '—'
    : `₦${parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Matches the server: VAT is charged per line, the adjustment is not taxed.
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const lineVat = (l) => round2((Number(l.estimated_amount) || 0) * (Number(l.vat_percentage) || 0) / 100);

const cellInput = {
  width: '100%', height: 34, padding: '5px 8px', border: '1px solid var(--border-light, #cbd5e1)',
  borderRadius: 5, fontSize: 12.5, background: 'var(--bg-surface, #ffffff)',
  color: 'var(--text-primary, #0f172a)', boxSizing: 'border-box', outline: 'none',
};
const cellInputNumber = {
  ...cellInput, textAlign: 'right', WebkitAppearance: 'none', MozAppearance: 'textfield', paddingRight: 8,
};
const th = {
  padding: '10px 10px', fontSize: 11, fontWeight: 800, textAlign: 'left',
  color: '#fff', background: '#0b318bff', whiteSpace: 'nowrap', letterSpacing: '0.02em',
  border: '1px solid black',
};
const thRight = { ...th, textAlign: 'right' };
const td = { padding: '6px 6px', border: '1px solid black', verticalAlign: 'middle' };

const blankLine = () => ({
  _new: true,
  item_id: null,
  material_id: '',
  material_other: '',
  description: '',
  unit_of_measure: '',
  quantity: '',
  estimated_unit_rate: '',
  estimated_amount: '',
  vat_percentage: '',
  project_id: '',
  vendor_id: '',
  expense_category_id: '',
  expense_sub_category_id: '',
  cost_control_quantity: '',
  cost_control_unit_rate: '',
  cost_control_amount: '',
  approved_quantity: '',
  approved_unit_rate: '',
  approved_amount: '',
  remarks: '',
});

const fromApi = (i) => ({
  _new: false,
  item_id: i.item_id || i.id,
  material_id: i.material_id ?? '',
  material_other: i.material_other ?? '',
  description: i.description ?? '',
  unit_of_measure: i.unit_of_measure ?? '',
  quantity: i.quantity ?? '',
  estimated_unit_rate: i.estimated_unit_rate ?? '',
  estimated_amount: i.estimated_amount ?? '',
  vat_percentage: i.vat_percentage ?? '',
  project_id: i.project_id ?? '',
  vendor_id: i.vendor_id ?? '',
  expense_category_id: i.expense_category_id ?? '',
  expense_sub_category_id: i.expense_sub_category_id ?? '',
  cost_control_quantity: i.cost_control_quantity ?? '',
  cost_control_unit_rate: i.cost_control_unit_rate ?? '',
  cost_control_amount: i.cost_control_amount ?? '',
  approved_quantity: i.approved_quantity ?? '',
  approved_unit_rate: i.approved_unit_rate ?? '',
  approved_amount: i.approved_amount ?? '',
  remarks: i.remarks ?? '',
});

// '' means "cleared", which is a real edit — so it is sent as null rather than
// dropped. undefined is what gets dropped, and nothing here produces it.
// The empty option. '—' reads as a value; this reads as an instruction.
const PICK = 'Select';

const num = (v) => (v === '' || v == null ? null : Number(v));
const str = (v) => (v === '' || v == null ? null : v);

export default function RequisitionDirectEdit() {
  const { prId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pr, setPr] = useState(null);
  const [header, setHeader] = useState({});
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState('');

  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      api.clearApiCache?.();
      const res = await api.getPR(prId);
      const data = res?.data || res;
      setPr(data);
      setHeader({
        title: data.title ?? '',
        description: data.description ?? '',
        requested_for: data.requested_for ?? '',
        required_date: (data.required_date || '').slice(0, 10),
        expected_due_date: (data.expected_due_date || '').slice(0, 10),
        priority_status: (data.priority_status || 'normal').toLowerCase(),
        remarks: data.remarks ?? '',
        adjustments_amount: data.adjustments_amount ?? '',
        requested_department_id: data.requested_department_id ?? '',
        purchase_request_type_id: data.purchase_request_type_id ?? '',
      });
      setLines((data.items || []).map(fromApi));
    } catch (e) {
      toast.show(errorText(e), 'error');
    } finally {
      setLoading(false);
    }
    // toast identity changes every render in some providers; the reload must
    // key off the requisition alone or this loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.allSettled([
      api.listProjects(), api.listSuppliers(), api.listExpenseCategories(),
      api.listExpenseSubCategories(), api.listMaterials(),
      api.listDepartments(0, 200, true), api.listPurchaseRequestTypes(true),
    ]).then(([p, v, c, sc, m, d, rt]) => {
      const val = (r) => (r.status === 'fulfilled' ? (Array.isArray(r.value) ? r.value : r.value?.data || []) : []);
      setProjects(val(p)); setVendors(val(v)); setCategories(val(c));
      setSubCategories(val(sc)); setMaterials(val(m));
      const dVal = d.status === 'fulfilled' ? (d.value?.departments || []) : [];
      const rtVal = rt.status === 'fulfilled' ? (rt.value?.types || []) : [];
      setDepartments(dVal); setRequestTypes(rtVal);
    });
  }, []);

  const setLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [field]: value };
      // Amount follows qty × rate, the way it does on every other requisition
      // grid — typed over only if somebody edits the amount itself.
      if (field === 'quantity' || field === 'estimated_unit_rate') {
        const q = Number(field === 'quantity' ? value : next.quantity);
        const r = Number(field === 'estimated_unit_rate' ? value : next.estimated_unit_rate);
        if (!isNaN(q) && !isNaN(r) && next.quantity !== '' && next.estimated_unit_rate !== '') {
          next.estimated_amount = round2(q * r);
        }
      }
      // Changing the category invalidates a sub-category that belonged to the
      // old one, so it is cleared rather than left pointing somewhere wrong.
      if (field === 'expense_category_id') next.expense_sub_category_id = '';
      // Picking a catalogue material retires the free-text one, so a line
      // never claims to be two different things at once.
      if (field === 'material_id' && value) next.material_other = '';
      return next;
    }));
  };

  const totals = useMemo(() => {
    const sum = lines.reduce((a, l) => a + (Number(l.estimated_amount) || 0), 0);
    const vat = lines.reduce((a, l) => a + lineVat(l), 0);
    const adj = Number(header.adjustments_amount) || 0;
    return { sum: round2(sum), vat: round2(vat), adj, total: round2(sum + vat + adj) };
  }, [lines, header.adjustments_amount]);

  const save = async () => {
    const bad = lines.findIndex((l) => l._new && (!l.project_id || !l.unit_of_measure));
    if (bad !== -1) {
      toast.show(`Line ${bad + 1} is new — it needs a project and a unit.`, 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await api.directEditPR(prId, {
        ...header,
        required_date: str(header.required_date),
        expected_due_date: str(header.expected_due_date),
        adjustments_amount: num(header.adjustments_amount),
        title: str(header.title),
        description: str(header.description),
        requested_for: str(header.requested_for),
        remarks: str(header.remarks),
        requested_department_id: str(header.requested_department_id),
        purchase_request_type_id: str(header.purchase_request_type_id),
        reason: str(reason),
        items: lines.map((l) => ({
          item_id: l.item_id || undefined,
          material_id: str(l.material_id),
          material_other: str(l.material_other),
          description: str(l.description),
          unit_of_measure: str(l.unit_of_measure),
          quantity: num(l.quantity),
          estimated_unit_rate: num(l.estimated_unit_rate),
          estimated_amount: num(l.estimated_amount),
          vat_percentage: num(l.vat_percentage),
          project_id: str(l.project_id),
          vendor_id: str(l.vendor_id),
          expense_category_id: str(l.expense_category_id),
          expense_sub_category_id: str(l.expense_sub_category_id),
          cost_control_quantity: num(l.cost_control_quantity),
          cost_control_unit_rate: num(l.cost_control_unit_rate),
          cost_control_amount: num(l.cost_control_amount),
          approved_quantity: num(l.approved_quantity),
          approved_unit_rate: num(l.approved_unit_rate),
          approved_amount: num(l.approved_amount),
          remarks: str(l.remarks),
        })),
      });
      toast.show(res?.message || 'Saved.', 'success');
      navigate(backTo);
    } catch (e) {
      toast.show(errorText(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card style={{ padding: 24, border: '1px solid black' }}><RNLLoader title="Loading requisition..." /></Card>;
  if (!pr) return <Card style={{ padding: 24, border: '1px solid black' }}><div style={{ padding: 8 }}>This requisition could not be loaded.</div></Card>;

  // The tab this page is reached from. Named once so Cancel, Back and a
  // successful save cannot drift to different destinations.
  // Where Back, Cancel and a successful save all return to. This page is
  // reachable from more than one list now, and dropping somebody on the wrong
  // tab after an edit means hunting for the requisition again.
  //
  // The tab id arrives as ?from= and is checked against this map rather than
  // being pasted into a URL: a value off the query string is untrusted, and an
  // unknown one would send the reader to a tab that renders nothing.
  const RETURN_TABS = {
    'approved-requisitions': 'Approved Requisitions',
    'approval-queue': 'Approval Queue',
  };
  const fromTab = RETURN_TABS[searchParams.get('from')] ? searchParams.get('from') : 'approved-requisitions';
  const backTo = `/enterprise-requisition?tab=${fromTab}`;
  const backLabel = `Back to ${RETURN_TABS[fromTab]}`;

  const subsFor = (categoryId) =>
    subCategories.filter((s) => !categoryId || String(s.expense_category_id) === String(categoryId));

  const readOnly = [
    ['REF ID', pr.pr_number],
    ['REQUESTER', pr.requested_by_name || '—'],
    ['STATUS', String(pr.request_status || '').toUpperCase()],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Back bar on the left, what is being edited on the right, one row.
          The title is right-aligned so it reads back from the page edge and
          cannot be mistaken for a heading over the banner below it. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 16, flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={() => { api.clearApiCache?.(); navigate(backTo); }}
          style={{
            padding: '8px 16px', borderRadius: 8, flexShrink: 0,
            border: '1px solid black', background: '#0b318bff', color: '#ffffff',
            fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center',
            gap: 6, fontSize: 13,
          }}
        >
          ← <span style={{ fontWeight: 900 }}>{backLabel}</span>
        </button>

        <div style={{ textAlign: 'right', minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Edit {pr.pr_number}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{pr.title || '—'}</div>
        </div>
      </div>

      {/* Said plainly and at the top: this is the difference between this
          screen and Recall, and it is the thing people will worry about. */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
        padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
      }}>
        <strong>This edits the record only.</strong> The approval stays exactly as it is —
        nothing moves through the workflow and nobody is asked to approve again. If the change
        is big enough that the decision no longer stands, use <strong>Recall</strong> instead.
        Every edit is written to the audit log.
      </div>

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 14 }}>
          SECTION 1 — REQUISITION HEADER
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14,
          padding: '12px 16px', background: 'var(--bg-base, #f8fafc)', borderRadius: 8,
          border: '1px solid var(--border-light, #e2e8f0)', marginBottom: 20
        }}>
          {readOnly.map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginTop: 3, color: 'var(--text-primary)' }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Purpose / Title" value={header.title}
                 onChange={(v) => setHeader({ ...header, title: v })} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Select label="Department"
                    value={header.requested_department_id || ''}
                    onChange={(v) => setHeader({ ...header, requested_department_id: v })}
                    options={[
                      { value: '', label: '— Select Department —' },
                      ...departments.map((d) => ({ value: d.id, label: d.name })),
                    ]} />
            <Select label="Request Type"
                    value={header.purchase_request_type_id || ''}
                    onChange={(v) => setHeader({ ...header, purchase_request_type_id: v })}
                    options={[
                      { value: '', label: '— Select Type —' },
                      ...requestTypes.map((t) => ({ value: t.id, label: t.name })),
                    ]} />
            <Input label="Requested For" value={header.requested_for}
                   onChange={(v) => setHeader({ ...header, requested_for: v })} />
            <Select label="Priority" value={header.priority_status}
                    onChange={(v) => setHeader({ ...header, priority_status: v })}
                    options={['low', 'normal', 'high', 'urgent'].map((p) => ({ value: p, label: p.toUpperCase() }))} />
            <DatePicker label="Required Date" value={header.required_date}
                   onChange={(v) => setHeader({ ...header, required_date: v })} />
            <DatePicker label="Expected Due Date" value={header.expected_due_date}
                   onChange={(v) => setHeader({ ...header, expected_due_date: v })} />
            <Input label="Description" as="textarea" rows={3} value={header.description}
                   onChange={(v) => setHeader({ ...header, description: v })} />
            <Input label="Remarks" as="textarea" rows={3} value={header.remarks}
                   onChange={(v) => setHeader({ ...header, remarks: v })} />
          </div>
        </div>
      </Card>

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' }}>
            SECTION 2 — LINE ITEMS
          </div>
          <Btn variant="outline" onClick={() => setLines([...lines, blankLine()])}>+ Add line</Btn>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{
            borderCollapse: 'collapse', fontSize: 12.5, minWidth: 2230,
            border: '1px solid black',
          }}>
            <thead>
              <tr>
                <th style={{ ...th, minWidth: 40, textAlign: 'center' }}>#</th>
                <th style={{ ...th, minWidth: 180 }}>MATERIAL</th>
                <th style={{ ...th, minWidth: 240 }}>DESCRIPTION</th>
                <th style={{ ...th, minWidth: 90 }}>UNIT</th>
                <th style={{ ...th, minWidth: 180 }}>PROJECT</th>
                <th style={{ ...th, minWidth: 180 }}>VENDOR</th>
                <th style={{ ...th, minWidth: 190 }}>EXPENSE CATEGORY</th>
                <th style={{ ...th, minWidth: 190 }}>SUB CATEGORY</th>
                <th style={{ ...thRight, minWidth: 90 }}>QTY</th>
                <th style={{ ...thRight, minWidth: 110 }}>EST. RATE</th>
                <th style={{ ...thRight, minWidth: 120 }}>EST. AMOUNT</th>
                <th style={{ ...thRight, minWidth: 80 }}>VAT %</th>
                <th style={{ ...thRight, minWidth: 100 }}>VAT</th>
                <th style={{ ...thRight, minWidth: 90, background: '#065f46' }}>CC QTY</th>
                <th style={{ ...thRight, minWidth: 110, background: '#065f46' }}>CC RATE</th>
                <th style={{ ...thRight, minWidth: 120, background: '#065f46' }}>CC AMOUNT</th>
                <th style={{ ...thRight, minWidth: 90, background: '#7c2d12' }}>APPR QTY</th>
                <th style={{ ...thRight, minWidth: 110, background: '#7c2d12' }}>APPR RATE</th>
                <th style={{ ...thRight, minWidth: 120, background: '#7c2d12' }}>APPR AMOUNT</th>
                <th style={{ ...th, minWidth: 180 }}>REMARKS</th>
                <th style={{ ...th, minWidth: 44 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l.item_id || `new-${idx}`} style={l._new ? { background: 'rgba(34,197,94,0.07)' } : undefined}>
                  <td style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600 }}>{idx + 1}</td>
                  <td style={td}>
                    <Select
                      title={`Material — line ${idx + 1}`}
                      value={l.material_id || ''}
                      onChange={(v) => setLine(idx, 'material_id', v)}
                      style={gridSelectStyle}
                      options={[
                        { value: '', label: PICK },
                        ...materials.map((m) => ({ value: m.id || m.material_id, label: m.name })),
                      ]}
                    />
                    {/* A line whose material was typed rather than picked keeps
                        that text visible, or the cell reads as empty and
                        somebody "fixes" it by overwriting what is there. */}
                    {!l.material_id && l.material_other && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                        {l.material_other}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <input style={cellInput} value={l.description}
                           onChange={(e) => setLine(idx, 'description', e.target.value)} />
                  </td>
                  <td style={td}>
                    <input style={cellInput} value={l.unit_of_measure}
                           onChange={(e) => setLine(idx, 'unit_of_measure', e.target.value)} />
                  </td>
                  <td style={td}>
                    <Select
                      title={`Project — line ${idx + 1}`}
                      value={l.project_id || ''}
                      onChange={(v) => setLine(idx, 'project_id', v)}
                      style={gridSelectStyle}
                      options={[{ value: '', label: PICK }, ...projects.map((p) => ({
                        value: p.id || p.project_id, label: p.project_name || p.name,
                      }))]}
                    />
                  </td>
                  <td style={td}>
                    <Select
                      title={`Vendor — line ${idx + 1}`}
                      value={l.vendor_id || ''}
                      onChange={(v) => setLine(idx, 'vendor_id', v)}
                      style={gridSelectStyle}
                      options={[{ value: '', label: PICK }, ...vendors.map((v) => ({
                        value: v.id || v.supplier_id, label: v.name,
                      }))]}
                    />
                  </td>
                  <td style={td}>
                    <Select
                      title={`Expense Category — line ${idx + 1}`}
                      value={l.expense_category_id || ''}
                      onChange={(v) => setLine(idx, 'expense_category_id', v)}
                      style={gridSelectStyle}
                      options={[{ value: '', label: PICK }, ...categories.map((c) => ({
                        value: c.id || c.expense_category_id,
                        label: c.code ? `${c.code} - ${c.name}` : c.name,
                      }))]}
                    />
                  </td>
                  <td style={td}>
                    {/* Disabled until a category is picked — a sub category
                        belongs to one, so offering the full list here would
                        let a line point at a child of a different parent. */}
                    <Select
                      title={`Sub Category — line ${idx + 1}`}
                      value={l.expense_sub_category_id || ''}
                      onChange={(v) => setLine(idx, 'expense_sub_category_id', v)}
                      disabled={!l.expense_category_id}
                      style={l.expense_category_id ? gridSelectStyle : gridSelectDisabledStyle}
                      options={[{ value: '', label: PICK }, ...subsFor(l.expense_category_id).map((sc) => ({
                        value: sc.expense_sub_category_id,
                        label: sc.code ? `${sc.code} - ${sc.name}` : sc.name,
                      }))]}
                    />
                  </td>
                  {['quantity', 'estimated_unit_rate', 'estimated_amount', 'vat_percentage'].map((f) => (
                    <td style={td} key={f}>
                      <input style={cellInputNumber} type="number" value={l[f]}
                             onChange={(e) => setLine(idx, f, e.target.value)} />
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)', fontWeight: 600, paddingRight: 10 }}>{money(lineVat(l))}</td>
                  {['cost_control_quantity', 'cost_control_unit_rate', 'cost_control_amount',
                    'approved_quantity', 'approved_unit_rate', 'approved_amount'].map((f) => (
                    <td style={td} key={f}>
                      <input style={cellInputNumber} type="number" value={l[f]}
                             onChange={(e) => setLine(idx, f, e.target.value)} />
                    </td>
                  ))}
                  <td style={td}>
                    <input style={cellInput} value={l.remarks}
                           onChange={(e) => setLine(idx, 'remarks', e.target.value)} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button
                      onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                      title="Remove this line"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#ef4444', padding: 4, lineHeight: 0,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={21} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                  No line items. Add one to continue.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border-light, #e2e8f0)' }}>
          <div style={{ width: 240 }}>
            <Input
              label="Adjustments (₦)"
              type="number"
              value={header.adjustments_amount}
              onChange={(v) => setHeader({ ...header, adjustments_amount: v })}
              placeholder="0.00"
            />
          </div>
          <div style={{ minWidth: 280, fontSize: 13 }}>
            {[['Items', totals.sum], ['VAT', totals.vat], ['Adjustments', totals.adj]].map(([label, v]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{money(v)}</span>
              </div>
            ))}
            <div style={{
              display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px',
              borderTop: '1.5px solid var(--border-light)', fontWeight: 800, fontSize: 15,
            }}>
              <span>TOTAL</span><span>{money(totals.total)}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
              Recalculated on save from the lines above.
            </div>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <Input
          label="Why is this being changed? (kept in the audit log)"
          as="textarea"
          rows={3}
          value={reason}
          onChange={setReason}
          placeholder="e.g. Unit rate was keyed in wrong at Cost Control."
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <Btn
            variant="ghost"
            onClick={() => navigate(backTo)}
            disabled={saving}
            style={{ background: '#ef4444', color: '#fff', border: '1px solid #b91c1c', fontWeight: 800 }}
          >
            Cancel
          </Btn>
          <Btn onClick={save} loading={saving}>Save changes</Btn>
        </div>
      </Card>
    </div>
  );
}
